import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import UrgencyBanner from "@/components/UrgencyBanner";
import { api } from "@/lib/api";

/**
 * AugustLanding — the /august route used exclusively for paid Instagram
 * traffic. Full-bleed drone hero, live scarcity banner above the fold, a
 * three-shot editorial film reel, testimonials, and a single deep-linked
 * Reserve CTA that carries UTM parameters straight into /booking.
 *
 * UTMs are read once from window.location.search on mount and forwarded to
 * the booking deep-link so the funnel remains attributable end-to-end.
 */
export default function AugustLanding() {
  const heroRef = useRef(null);
  const [urgency, setUrgency] = useState(null);
  const [testimonials, setTestimonials] = useState([]);

  // Pull the live availability so the top-of-page count matches the CTA link.
  useEffect(() => {
    api.get("/availability/urgency").then((r) => setUrgency(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials((r.data || []).slice(0, 3))).catch(() => {});
  }, []);

  // Preserve incoming UTM params through to the booking page so Meta Ads
  // Manager can attribute conversions properly.
  const utmSuffix = useMemo(() => {
    if (typeof window === "undefined") return "";
    const p = new URLSearchParams(window.location.search);
    const keep = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
    const kept = keep.map((k) => (p.get(k) ? `${k}=${encodeURIComponent(p.get(k))}` : null)).filter(Boolean);
    return kept.length ? `&${kept.join("&")}` : "";
  }, []);

  const bookingHref = urgency?.next_range
    ? `/booking?checkin=${urgency.next_range.checkin}&checkout=${urgency.next_range.checkout}${utmSuffix}`
    : `/booking${utmSuffix ? `?${utmSuffix.slice(1)}` : ""}`;

  return (
    <div className="bg-cream text-ionian">
      <Nav />

      {/* HERO — full-bleed drone loop, urgency + CTA overlaid */}
      <section className="relative h-[90vh] min-h-[600px] w-full overflow-hidden bg-ionian">
        <video
          ref={heroRef}
          src="/films/kioni-arrival.mp4"
          poster="/films/kioni-arrival-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="august-hero-video"
        />
        {/* Layered overlays: brand-tinted wash + strong bottom vignette for legibility */}
        <div className="absolute inset-0 bg-ionian/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-ionian/20 via-ionian/40 to-ionian/95" />

        <div className="relative z-10 h-full flex flex-col justify-end pb-16 md:pb-24 px-6 md:px-16 max-w-[1400px] mx-auto">
          <div className="text-cream/80 text-[10px] md:text-xs tracking-[0.35em] uppercase mb-4 drop-shadow-md">
            Kioni · Ithaca · Ionian Sea
          </div>
          <h1 className="text-cream font-sans text-4xl md:text-6xl lg:text-7xl font-extralight leading-[1.05] tracking-tight max-w-3xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]">
            A quiet garden above the harbour.
          </h1>
          <p className="text-cream/90 mt-6 text-base md:text-lg font-light max-w-xl leading-relaxed drop-shadow-md">
            A private summer villa on the island Odysseus called home.<br />
            Direct booking · No agency fees · Sleeps four.
          </p>
          <div className="mt-8 flex flex-col md:flex-row gap-4 md:items-center">
            <Link
              to={bookingHref}
              data-testid="august-hero-cta"
              className="inline-flex items-center gap-2 px-8 py-4 bg-cream text-ionian hover:bg-brass hover:text-cream transition-colors font-sans text-sm tracking-[0.18em] uppercase w-fit shadow-lg font-medium"
            >
              Reserve August dates
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M5 12h14M13 5l7 7-7 7"/>
              </svg>
            </Link>
            {urgency && !urgency.in_past && (urgency.hook?.available_nights ?? urgency.available_nights) > 0 && (
              <div className="text-cream/90 text-sm md:pl-6 md:border-l md:border-cream/30 drop-shadow-md">
                <span className="font-serif text-sand text-2xl align-middle tabular-nums">{urgency.hook?.available_nights ?? urgency.available_nights}</span>{" "}
                nights still open in {urgency.hook?.label || "August"} · from{" "}
                <span className="tabular-nums">€{Math.round(urgency.next_range?.avg_nightly_rate || 390)}</span> / night
              </div>
            )}
          </div>
        </div>
      </section>

      {/* URGENCY WIDGET */}
      <UrgencyBanner />

      {/* THREE ANGLES — supporting films */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-16 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mb-12 md:mb-16">
          <div className="md:col-span-5">
            <div className="overline">The Island</div>
            <h2 className="font-sans text-4xl md:text-5xl font-extralight mt-6 leading-[1.05] tracking-tight">
              Ithaca, from three angles.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-lg leading-[1.9] text-ionian/80 font-light">
              A working fishing village, a coastline unchanged since the Odyssey,
              and a whitewashed chapel above the harbour bells. This is where
              you'll spend August.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-testid="august-films">
          <SmartFilm src="/films/church-harbour.mp4" poster="/films/church-harbour-poster.jpg" caption="The chapel bell above Kioni harbour" />
          <SmartFilm src="/films/coastline.mp4" poster="/films/coastline-poster.jpg" caption="A private stretch of Ionian coastline" />
          <SmartFilm src="/films/villa-tour.mp4" poster="/films/villa-tour-poster.jpg" caption="Every room, every corner — a minute inside" />
        </div>
      </section>

      {/* WHY DIRECT */}
      <section className="bg-stone_white border-y border-soft_border">
        <div className="max-w-[1200px] mx-auto px-6 md:px-16 py-20 md:py-24">
          <div className="text-center max-w-3xl mx-auto">
            <div className="overline">Book Direct</div>
            <h2 className="font-sans text-3xl md:text-4xl font-extralight mt-6 leading-[1.15]">
              Why guests skip the OTAs.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mt-14">
            {[
              ["No booking fees", "You pay the villa, not the platform. That's a 15–18% saving on every night."],
              ["Owner-hosted", "Deal directly with the family who own the villa. Every question answered by name."],
              ["Flexible terms", "Deposit-only reservations, seasonal rates, referral discounts. Impossible on Airbnb."],
            ].map(([title, body]) => (
              <div key={title}>
                <div className="text-brass font-serif text-2xl">— {title}</div>
                <p className="mt-3 text-ionian/75 font-light leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS (only if we have any) */}
      {testimonials.length > 0 && (
        <section className="max-w-[1200px] mx-auto px-6 md:px-16 py-20 md:py-28">
          <div className="overline text-center">In their own words</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mt-10">
            {testimonials.map((t, i) => (
              <figure key={i} className="border-l border-brass pl-6" data-testid={`august-testimonial-${i}`}>
                <blockquote className="font-serif text-xl leading-relaxed text-ionian/90 italic">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-4 text-sm text-ionian/60 tracking-wide">
                  — {t.display_name}{t.period ? ` · ${t.period}` : ""}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="bg-ionian text-cream">
        <div className="max-w-[1000px] mx-auto px-6 md:px-16 py-24 md:py-32 text-center">
          <div className="overline text-cream/60">The Odyssey ends here</div>
          <h2 className="font-sans text-4xl md:text-5xl font-extralight mt-6 leading-[1.1]">
            Reserve your dates before someone else does.
          </h2>
          {urgency && !urgency.fully_booked && !urgency.in_past && urgency.next_range && (
            <p className="mt-8 text-cream/80 text-lg font-light">
              Longest window still open ·{" "}
              <span className="font-serif text-sand">
                {formatShortRange(urgency.next_range.checkin, urgency.next_range.checkout)}
              </span>{" "}
              · {urgency.next_range.nights} nights from €{Math.round(urgency.next_range.avg_nightly_rate || 390)} / night
            </p>
          )}
          <Link
            to={bookingHref}
            data-testid="august-footer-cta"
            className="inline-flex items-center gap-2 mt-10 px-10 py-4 bg-cream text-ionian hover:bg-brass hover:text-cream transition-colors font-sans text-sm tracking-[0.2em] uppercase font-medium"
          >
            Reserve now
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M5 12h14M13 5l7 7-7 7"/>
            </svg>
          </Link>
          <p className="mt-6 text-cream/50 text-xs tracking-widest uppercase">
            Secured by Stripe · No booking fee
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// -----------------------------------------------------------------------------
// SmartFilm — autoplays muted-looping on scroll into view; native controls
// remain accessible.

function SmartFilm({ src, poster, caption }) {
  const ref = useRef(null);
  useEffect(() => {
    const vid = ref.current;
    if (!vid) return;
    const tryPlay = () => {
      const p = vid.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { vid.preload = "auto"; tryPlay(); }
        else vid.pause();
      },
      { threshold: 0.2, rootMargin: "0px 0px -10% 0px" },
    );
    io.observe(vid);
    vid.addEventListener("canplay", tryPlay);
    return () => { io.disconnect(); vid.removeEventListener("canplay", tryPlay); };
  }, []);
  return (
    <figure className="border border-soft_border overflow-hidden">
      <video
        ref={ref}
        src={src}
        poster={poster}
        muted loop playsInline autoPlay preload="metadata"
        className="w-full aspect-video bg-black object-cover"
        controls
      />
      <figcaption className="px-4 py-3 text-sm text-ionian/70 italic border-t border-soft_border bg-stone_white">
        {caption}
      </figcaption>
    </figure>
  );
}

function formatShortRange(ci, co) {
  const a = new Date(ci + "T12:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  const b = new Date(co + "T12:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return `${a} → ${b}`;
}
